// =============================================================================
// app/settings/language.tsx — locale picker (bare-RN port).
//
// MIGRATION (P3 settings): @expo/vector-icons -> components/ui/Icon; the inline
// expo-router <Stack.Screen options> headers are dropped (SettingsStack owns the
// title). All API/business logic is unchanged.
//
// APPLE-ALIGNED PASS: full-bleed ParticleBackground replaces the flat lavender
// fill (mounted first, under the stack header per skill §12 — this is a nested
// settings list that keeps the stack header, so the aurora shows in the body).
// The flat list becomes a single GlassCard of rows: each row carries a globe
// icon chip, the English name plus its endonym (native name) as a subtitle for
// hierarchy from weight+size (§15), a hairline divider, and a selected pill with
// a check. Section header gains a title+subtitle; typography tightened. All
// API/business logic (GET /settings, PUT /settings { locale }) is unchanged.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassCard } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';

// The backend stores a single BCP-47 locale at profile.locale (e.g. "en-US",
// "es-ES"). Each option below is the full locale we send/receive. `native` is
// the language's endonym, shown as a subtitle for craft + at-a-glance scanning.
const LANGUAGES = [
  { code: 'en-US', label: 'English', native: 'English' },
  { code: 'es-ES', label: 'Spanish', native: 'Español' },
  { code: 'pt-BR', label: 'Portuguese', native: 'Português' },
  { code: 'fr-FR', label: 'French', native: 'Français' },
  { code: 'de-DE', label: 'German', native: 'Deutsch' },
  { code: 'ja-JP', label: 'Japanese', native: '日本語' },
  { code: 'ko-KR', label: 'Korean', native: '한국어' },
  { code: 'zh-CN', label: 'Chinese', native: '中文' },
  { code: 'ar-SA', label: 'Arabic', native: 'العربية' },
  { code: 'hi-IN', label: 'Hindi', native: 'हिन्दी' },
];

interface SettingsResponse {
  profile?: { locale?: string };
}

export default function LanguageScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [language, setLanguage] = useState('en-US');

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    // Locale lives under profile.locale on GET /settings, not at the top level.
    const res = await api<SettingsResponse>('/settings');
    if (res.success && res.data?.profile?.locale) {
      const locale = res.data.profile.locale;
      // Match the exact locale, else fall back by language prefix (e.g. "es").
      const exact = LANGUAGES.find((l) => l.code === locale);
      const byPrefix = LANGUAGES.find(
        (l) => l.code.split('-')[0] === locale.split('-')[0]
      );
      setLanguage((exact || byPrefix)?.code || 'en-US');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  function handleLanguageSelect(code: string) {
    const prev = language;
    setLanguage(code);
    setSaving(true);
    // Backend defines PUT /settings (no PATCH at the root) — using PATCH 404s.
    // The only writable region field is `locale` (top-level); it updates
    // profile.locale and mirrors into preferences.language.
    api('/settings', {
      method: 'PUT',
      body: JSON.stringify({ locale: code }),
    }).then((res) => {
      setSaving(false);
      if (!res.success) {
        setLanguage(prev); // revert on failure
        Alert.alert('Error', res.error || 'Failed to save language.');
      }
    });
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, containerStyle]}
        showsVerticalScrollIndicator={false}
      >
        {/* Language */}
        <View style={styles.group}>
          <View style={styles.groupHeader}>
            <Text style={styles.sectionTitle}>App Language</Text>
            <Text style={styles.sectionSubtitle}>How CRYB speaks to you</Text>
          </View>
          <GlassCard padding={0}>
            {LANGUAGES.map((lang, idx) => {
              const active = language === lang.code;
              return (
                <TouchableOpacity
                  key={lang.code}
                  style={[
                    styles.optionRow,
                    idx < LANGUAGES.length - 1 && styles.optionBorder,
                  ]}
                  onPress={() => handleLanguageSelect(lang.code)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel={lang.label}
                  accessibilityState={{ selected: active }}
                >
                  <View style={[styles.iconWrap, active && styles.iconWrapActive]}>
                    <Ionicons
                      name="globe-outline"
                      size={19}
                      color={active ? colors.primary : colors.muted}
                    />
                  </View>
                  <View style={styles.optionText}>
                    <Text
                      style={[styles.optionLabel, active && styles.optionLabelActive]}
                      numberOfLines={1}
                    >
                      {lang.label}
                    </Text>
                    <Text style={styles.optionNative} numberOfLines={1}>
                      {lang.native}
                    </Text>
                  </View>
                  {active && (
                    <View style={styles.checkPill}>
                      <Ionicons name="checkmark" size={16} color={colors.white} />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </GlassCard>
        </View>

        {saving && (
          <View style={styles.savingOverlay}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.savingText}>Saving…</Text>
          </View>
        )}

        <View style={{ height: spacing.xxxl }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    padding: spacing.lg,
  },
  group: {
    marginBottom: spacing.xl,
  },
  groupHeader: {
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
    marginTop: spacing.lg,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: colors.faint,
    marginTop: 2,
    ...fonts.regular,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  optionBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconWrapActive: {
    backgroundColor: colors.primary + '1A',
  },
  optionText: {
    flex: 1,
  },
  optionLabel: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.medium,
  },
  optionLabelActive: {
    color: colors.primary,
    ...fonts.semibold,
  },
  optionNative: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
    ...fonts.regular,
  },
  checkPill: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  savingOverlay: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  savingText: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.medium,
  },
});
